// Vendors — who Rootegra pays, where each one's bills usually post, and the
// share of a shared bill that is Rootegra's. The share is written to the Apple
// tab's settings at the same time so the waterfall and the ledger agree.
import { useEffect, useState } from 'react';
import { useBooksAccess } from '../../contexts/AuthContext';
import { ConfirmButton, Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { supabase } from '../../lib/supabase';
import { vendors as vendorsApi } from '../../lib/ledger/api';
import type { Account, Vendor } from '../../lib/ledger/types';
import { AccountSelect, ReadOnlyNote, useAccounts } from './shared';

function useLoad<T>(fn: () => Promise<T>, deps: readonly unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}

export function slugify(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/** Keep books_settings.vendor_shares[slug] equal to the vendor's share so the Apple waterfall never drifts from the ledger. */
async function syncVendorShare(slug: string, share: number | null): Promise<void> {
  const r = await supabase.from('books_settings').select('value').eq('key', 'vendor_shares').maybeSingle();
  if (r.error) throw new Error(r.error.message);
  const value: Record<string, number> = { ...((r.data?.value as Record<string, number> | null) ?? {}) };
  if (share == null) delete value[slug]; else value[slug] = share;
  const w = await supabase.from('books_settings').upsert({ key: 'vendor_shares', value });
  if (w.error) throw new Error(w.error.message);
}

type Draft = { name: string; slug: string; slugTouched: boolean; default_account_id: string; share_pct: string; match_text: string; notes: string };

const EMPTY: Draft = { name: '', slug: '', slugTouched: false, default_account_id: '', share_pct: '', match_text: '', notes: '' };

function toDraft(v: Vendor): Draft {
  return {
    name: v.name, slug: v.slug, slugTouched: true, default_account_id: v.default_account_id ?? '',
    share_pct: v.share_pct == null ? '' : String(v.share_pct), match_text: (v.match_text ?? []).join(', '), notes: v.notes ?? '',
  };
}

function parseShare(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0 || n > 100) throw new Error('The share must be a number from 0 to 100.');
  return Math.round(n * 100) / 100;
}

export function VendorsTab() {
  const { canEdit } = useBooksAccess();
  const chart = useAccounts();
  const list = useLoad(() => vendorsApi.list(), []);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  const rows = list.data ?? [];

  async function save(d: Draft, existing: Vendor | null) {
    setBusy(true); setErr(null); setDone(null);
    try {
      const name = d.name.trim();
      if (!name) throw new Error('Give the vendor a name.');
      const slug = existing ? existing.slug : slugify(d.slug || name);
      if (!slug) throw new Error('The slug needs at least one letter or number.');
      if (!existing && rows.some((v) => v.slug === slug)) throw new Error(`A vendor with the slug "${slug}" already exists. Edit that one instead.`);
      const share = parseShare(d.share_pct);
      await vendorsApi.upsert({
        ...(existing ? { id: existing.id, is_active: existing.is_active } : { is_active: true }),
        slug,
        name,
        default_account_id: d.default_account_id || null,
        share_pct: share,
        match_text: d.match_text.split(',').map((s) => s.trim()).filter(Boolean),
        notes: d.notes.trim() || null,
      });
      await syncVendorShare(slug, share);
      setDone(`${existing ? 'Saved' : 'Added'} ${name}.${share != null ? ` The Apple tab now splits ${name} at ${share}%.` : ''}`);
      setEditingId(null);
      setAdding(false);
      setDraft(EMPTY);
      list.reload();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  async function setActive(v: Vendor, on: boolean) {
    setErr(null); setDone(null);
    try {
      await vendorsApi.upsert({ id: v.id, slug: v.slug, name: v.name, is_active: on });
      setDone(`${v.name} is ${on ? 'active again' : 'no longer active'}.`);
      list.reload();
    } catch (e) { setErr(e); }
  }

  const accountWords = (id: string | null) => {
    if (!id) return '—';
    const a = chart.byId.get(id);
    return a ? `${a.code} · ${a.name}` : '—';
  };

  return (
    <>
      {!canEdit && <ReadOnlyNote />}
      <Section
        title="Vendors"
        right={<button type="button" className="lp-btn lp-btn--quiet lp-no-print" disabled={!canEdit} onClick={() => { setAdding((v) => !v); setDraft(EMPTY); }}>{adding ? 'Close' : 'Add a vendor'}</button>}
      >
        <ErrorNote error={chart.error} />
        <ErrorNote error={list.error} />
        <ErrorNote error={err} />
        {done && <Note tone="good">{done}</Note>}
        {adding && canEdit && (
          <DraftForm
            d={draft}
            onChange={setDraft}
            accounts={chart.accounts}
            busy={busy}
            isNew
            onSave={() => void save(draft, null)}
            onCancel={() => { setAdding(false); setDraft(EMPTY); }}
          />
        )}
        {list.loading ? <Empty>Loading vendors…</Empty> : rows.length === 0 ? <Empty>No vendors yet.</Empty> : (
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead>
                <tr><th>Name</th><th>Slug</th><th>Default account</th><th className="num">Rootegra share</th><th>Match text</th><th>Status</th><th className="lp-no-print" /></tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  editingId === v.id ? (
                    <tr key={v.id}>
                      <td colSpan={7}>
                        <DraftForm
                          d={edit}
                          onChange={setEdit}
                          accounts={chart.accounts}
                          busy={busy}
                          onSave={() => void save(edit, v)}
                          onCancel={() => setEditingId(null)}
                        />
                      </td>
                    </tr>
                  ) : (
                    <tr key={v.id} className={v.is_active ? undefined : 'quiet'}>
                      <td>
                        <div>{v.name}</div>
                        {v.notes && <div className="muted">{v.notes}</div>}
                      </td>
                      <td><code>{v.slug}</code></td>
                      <td>{accountWords(v.default_account_id)}</td>
                      <td className="num">{v.share_pct == null ? <span className="muted">—</span> : `${Number(v.share_pct)}%`}</td>
                      <td className="muted">{(v.match_text ?? []).join(', ') || '—'}</td>
                      <td><span className={`lp-tag ${v.is_active ? 'lp-tag--good' : ''}`}>{v.is_active ? 'Active' : 'Inactive'}</span></td>
                      <td className="lp-no-print">
                        <div className="lp-row">
                          <button type="button" className="lp-btn lp-btn--small" disabled={!canEdit || busy} onClick={() => { setEditingId(v.id); setEdit(toDraft(v)); setAdding(false); }}>Edit</button>
                          {v.is_active
                            ? <ConfirmButton label="Deactivate" sentence={`Deactivate ${v.name}? Old entries keep the name; it just leaves the pick lists.`} yes="Yes, deactivate" disabled={!canEdit || busy} onConfirm={() => setActive(v, false)} />
                            : <button type="button" className="lp-btn lp-btn--small" disabled={!canEdit || busy} onClick={() => void setActive(v, true)}>Reactivate</button>}
                        </div>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted">Saving a share here also updates the Apple tab's vendor shares, so the monthly waterfall and the ledger split a bill the same way. Vendors are never deleted; deactivate one you no longer use so old entries keep their name.</p>
      </Section>
    </>
  );
}

function DraftForm({ d, onChange, accounts, busy, isNew = false, onSave, onCancel }: {
  d: Draft;
  onChange: (d: Draft) => void;
  accounts: Account[];
  busy: boolean;
  isNew?: boolean;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="lp-form" style={{ marginBottom: 12 }}>
      <div className="lp-form-grid">
        <Field label="Name">
          <input
            className="lp-inp"
            value={d.name}
            onChange={(e) => onChange({ ...d, name: e.target.value, slug: isNew && !d.slugTouched ? slugify(e.target.value) : d.slug })}
            placeholder="Supabase"
          />
        </Field>
        <Field label="Slug" hint={isNew ? 'Lowercase with underscores. It cannot change later.' : 'Fixed once the vendor exists.'}>
          <input className="lp-inp" value={d.slug} disabled={!isNew} onChange={(e) => onChange({ ...d, slug: slugify(e.target.value) || e.target.value.toLowerCase(), slugTouched: true })} />
        </Field>
        <Field label="Default account"><AccountSelect value={d.default_account_id} onChange={(id) => onChange({ ...d, default_account_id: id })} accounts={accounts} types={['expense', 'cogs']} /></Field>
        <Field label="Rootegra share %" hint="Leave blank when Rootegra pays the whole bill.">
          <input className="lp-inp lp-money" inputMode="decimal" value={d.share_pct} onChange={(e) => onChange({ ...d, share_pct: e.target.value })} placeholder="100" />
        </Field>
        <Field label="Match text" hint="Comma-separated words found in the bank description.">
          <input className="lp-inp" value={d.match_text} onChange={(e) => onChange({ ...d, match_text: e.target.value })} placeholder="supabase, supabase pte" />
        </Field>
        <Field label="Notes"><input className="lp-inp" value={d.notes} onChange={(e) => onChange({ ...d, notes: e.target.value })} placeholder="Optional" /></Field>
      </div>
      <div className="lp-row">
        <button type="button" className="lp-btn lp-btn--primary" disabled={busy || !d.name.trim()} onClick={onSave}>{busy ? 'Saving…' : isNew ? 'Add vendor' : 'Save'}</button>
        <button type="button" className="lp-btn lp-btn--quiet" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
